import { jsx } from "react/jsx-runtime";
import {
  composeEventHandlers,
  styled,
  themeable,
  useEvent,
  withStaticProperties
} from "@tamagui/core";
import { ThemeableStack } from "@tamagui/stacks";
import { SizableText } from "@tamagui/text";
import { useControllableState } from "@tamagui/use-controllable-state";
import * as React from "react";
import { TOAST_NAME } from "./constants";
import { ToastAnnounceExclude } from "./ToastAnnounce";
import {
  useToast,
  useToastController,
  useToastState
} from "./ToastImperative";
import {
  ToastImpl,
  ToastImplFrame,
  useToastInteractiveContext
} from "./ToastImpl";
import {
  ToastProvider,
  createToastScope
} from "./ToastProvider";
import { ToastViewport } from "./ToastViewport";
const TITLE_NAME = "ToastTitle";
const ToastTitleFrame = styled(SizableText, {
  name: TITLE_NAME,
  variants: {
    unstyled: {
      false: {
        color: "$color",
        size: "$4"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const ToastTitle = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeToast, ...titleProps } = props;
    return /* @__PURE__ */ jsx(ToastTitleFrame, { ...titleProps, ref: forwardedRef });
  }
);
ToastTitle.displayName = TITLE_NAME;
const DESCRIPTION_NAME = "ToastDescription";
const ToastDescriptionFrame = styled(SizableText, {
  name: DESCRIPTION_NAME,
  variants: {
    unstyled: {
      false: {
        color: "$color11",
        size: "$1"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const ToastDescription = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeToast, ...descriptionProps } = props;
    return /* @__PURE__ */ jsx(ToastDescriptionFrame, { ...descriptionProps, ref: forwardedRef });
  }
);
ToastDescription.displayName = DESCRIPTION_NAME;
const ACTION_NAME = "ToastAction";
const ToastAction = React.forwardRef(
  (props, forwardedRef) => {
    const { altText, ...actionProps } = props;
    if (!altText)
      return null;
    return /* @__PURE__ */ jsx(ToastAnnounceExclude, { altText, asChild: true, children: /* @__PURE__ */ jsx(ToastClose, { ...actionProps, ref: forwardedRef }) });
  }
);
ToastAction.propTypes = {
  altText(props) {
    if (!props.altText) {
      return new Error(`Missing prop \`altText\` expected on \`${ACTION_NAME}\``);
    }
    return null;
  }
};
ToastAction.displayName = ACTION_NAME;
const CLOSE_NAME = "ToastClose";
const ToastCloseFrame = styled(ThemeableStack, {
  name: CLOSE_NAME,
  tag: "button"
});
const ToastClose = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeToast, ...closeProps } = props;
    const interactiveContext = useToastInteractiveContext(CLOSE_NAME, __scopeToast);
    return /* @__PURE__ */ jsx(ToastAnnounceExclude, { asChild: true, children: /* @__PURE__ */ jsx(
      ToastCloseFrame,
      {
        accessibilityLabel: "Dialog Close",
        ...closeProps,
        ref: forwardedRef,
        onPress: composeEventHandlers(props.onPress, interactiveContext.onClose)
      }
    ) });
  }
);
ToastClose.displayName = CLOSE_NAME;
const ToastComponent = React.forwardRef(
  ToastImplFrame.extractable((props, forwardedRef) => {
    const { forceMount, open: openProp, defaultOpen, onOpenChange, ...toastProps } = props;
    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen ?? true,
      onChange: onOpenChange,
      strategy: "most-recent-wins"
    });
    const id = React.useId();
    const onPause = useEvent(props.onPause);
    const onResume = useEvent(props.onResume);
    const shouldShow = forceMount || open;
    if (!shouldShow)
      return null;
    return /* @__PURE__ */ jsx(
      ToastImpl,
      {
        id,
        open,
        ...toastProps,
        ref: forwardedRef,
        onClose: () => setOpen(false),
        onPause,
        onResume,
        onSwipeEnd: composeEventHandlers(props.onSwipeEnd, (event) => {
          setOpen(false);
        })
      }
    );
  })
);
ToastComponent.displayName = TOAST_NAME;
const Toast = withStaticProperties(
  themeable(ToastComponent, ToastImplFrame.staticConfig),
  {
    Title: ToastTitle,
    Description: ToastDescription,
    Action: ToastAction,
    Close: ToastClose
  }
);
export {
  Toast,
  ToastProvider,
  ToastViewport,
  createToastScope,
  useToast,
  useToastController,
  useToastState
};
//# sourceMappingURL=Toast.js.map
